import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  MapPin, 
  Monitor, 
  Smartphone, 
  Clock, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Globe,
  Wifi,
  Battery,
  MousePointer,
  Keyboard,
  Eye
} from 'lucide-react';

interface LoginAttempt {
  id: string;
  user_id?: string | null;
  phone_number?: string | null;
  email?: string | null;
  attempt_type: string;
  success: boolean;
  ip_address?: string | null;
  user_agent?: string | null;
  location_data?: any;
  device_fingerprint?: any;
  session_data?: any;
  risk_score: number;
  created_at: string;
  updated_at: string;
}

interface LoginAttemptDetailsProps {
  attempt: LoginAttempt;
  onBack: () => void;
}

export const LoginAttemptDetails = ({ attempt, onBack }: LoginAttemptDetailsProps) => {
  const [showRawData, setShowRawData] = useState(false);

  const getRiskBadgeVariant = (score: number) => {
    if (score >= 75) return 'destructive';
    if (score >= 50) return 'default';
    if (score >= 25) return 'secondary';
    return 'outline';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 75) return 'Critical Risk';
    if (score >= 50) return 'High Risk';
    if (score >= 25) return 'Medium Risk';
    return 'Low Risk';
  };

  const formatUserAgent = (userAgent?: string | null) => {
    if (!userAgent) return {
      browser: 'Unknown Browser',
      os: 'Unknown OS',
      full: 'Unknown'
    };
    
    // Extract browser and OS info
    const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/);
    const osMatch = userAgent.match(/(Windows NT|Mac OS X|Linux|Android|iOS)/);
    
    return {
      browser: browserMatch ? browserMatch[0] : 'Unknown Browser',
      os: osMatch ? osMatch[0] : 'Unknown OS',
      full: userAgent
    };
  };

  const userAgentInfo = formatUserAgent(attempt.user_agent);
  const deviceInfo = attempt.device_fingerprint || {};
  const locationInfo = attempt.location_data || {};
  const sessionInfo = attempt.session_data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Login Attempt Details</h2>
            <p className="text-muted-foreground">
              {attempt.phone_number || attempt.email || 'Unknown user'} • {new Date(attempt.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {attempt.success ? (
            <CheckCircle className="h-6 w-6 text-green-600" />
          ) : (
            <XCircle className="h-6 w-6 text-red-600" />
          )}
          <Badge variant={getRiskBadgeVariant(attempt.risk_score)}>
            {getRiskLabel(attempt.risk_score)} ({attempt.risk_score})
          </Badge>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Attempt Status</CardTitle>
            {attempt.success ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${attempt.success ? 'text-green-600' : 'text-red-600'}`}>
              {attempt.success ? 'Success' : 'Failed'}
            </div>
            <p className="text-xs text-muted-foreground">{attempt.attempt_type.toUpperCase()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Risk Score</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{attempt.risk_score}</div>
            <p className="text-xs text-muted-foreground">{getRiskLabel(attempt.risk_score)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">IP Address</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{attempt.ip_address || 'Unknown'}</div>
            <p className="text-xs text-muted-foreground">Network Location</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Session Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sessionInfo.timeOnPage ? `${Math.round(sessionInfo.timeOnPage / 1000)}s` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">Time on page</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information Tabs */}
      <Tabs defaultValue="device" className="space-y-4">
        <TabsList>
          <TabsTrigger value="device">Device Info</TabsTrigger>
          <TabsTrigger value="location">Location</TabsTrigger>
          <TabsTrigger value="session">Session Data</TabsTrigger>
          <TabsTrigger value="security">Security Analysis</TabsTrigger>
          <TabsTrigger value="raw">Raw Data</TabsTrigger>
        </TabsList>

        <TabsContent value="device" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Monitor className="h-5 w-5" />
                  <span>Browser & System</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Browser:</span>
                    <div className="text-muted-foreground">{userAgentInfo.browser}</div>
                  </div>
                  <div>
                    <span className="font-medium">Platform:</span>
                    <div className="text-muted-foreground">{deviceInfo.platform || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Language:</span>
                    <div className="text-muted-foreground">{deviceInfo.language || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Timezone:</span>
                    <div className="text-muted-foreground">{deviceInfo.timezone || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Screen:</span>
                    <div className="text-muted-foreground">{deviceInfo.screenResolution || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Color Depth:</span>
                    <div className="text-muted-foreground">{deviceInfo.colorDepth || 'Unknown'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Smartphone className="h-5 w-5" />
                  <span>Device Capabilities</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Touch Support:</span>
                    <div className="text-muted-foreground">{deviceInfo.touchSupport ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Cookies:</span>
                    <div className="text-muted-foreground">{deviceInfo.cookieEnabled ? 'Enabled' : 'Disabled'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Do Not Track:</span>
                    <div className="text-muted-foreground">{deviceInfo.doNotTrack ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <span className="font-medium">CPU Cores:</span>
                    <div className="text-muted-foreground">{deviceInfo.hardwareConcurrency || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="font-medium">Memory:</span>
                    <div className="text-muted-foreground">{deviceInfo.deviceMemory ? `${deviceInfo.deviceMemory}GB` : 'Unknown'}</div>
                  </div>
                  {deviceInfo.batteryLevel && (
                    <div>
                      <span className="font-medium">Battery:</span>
                      <div className="text-muted-foreground flex items-center">
                        <Battery className="h-3 w-3 mr-1" />
                        {deviceInfo.batteryLevel}%
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {deviceInfo.webglVendor && (
            <Card>
              <CardHeader>
                <CardTitle>WebGL Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Vendor:</span>
                    <div className="text-muted-foreground">{deviceInfo.webglVendor}</div>
                  </div>
                  <div>
                    <span className="font-medium">Renderer:</span>
                    <div className="text-muted-foreground">{deviceInfo.webglRenderer}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="location" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>Geographic Location</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {locationInfo.latitude && locationInfo.longitude ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Latitude:</span>
                        <div className="text-muted-foreground">{locationInfo.latitude.toFixed(6)}</div>
                      </div>
                      <div>
                        <span className="font-medium">Longitude:</span>
                        <div className="text-muted-foreground">{locationInfo.longitude.toFixed(6)}</div>
                      </div>
                      {locationInfo.accuracy && (
                        <div className="col-span-2">
                          <span className="font-medium">Accuracy:</span>
                          <div className="text-muted-foreground">{Math.round(locationInfo.accuracy)} meters</div>
                        </div>
                      )}
                    </div>
                    <div className="h-48 bg-muted rounded-lg flex items-center justify-center">
                      <span className="text-muted-foreground">Map would be displayed here</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No location data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Wifi className="h-5 w-5" />
                  <span>Network Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">IP Address:</span>
                    <div className="text-muted-foreground">{attempt.ip_address || 'Unknown'}</div>
                  </div>
                  {deviceInfo.connectionType && (
                    <div>
                      <span className="font-medium">Connection Type:</span>
                      <div className="text-muted-foreground">{deviceInfo.connectionType}</div>
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Timezone:</span>
                    <div className="text-muted-foreground">{locationInfo.timezone || deviceInfo.timezone || 'Unknown'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="session" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MousePointer className="h-5 w-5" />
                  <span>User Behavior</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Mouse Movements:</span>
                    <div className="text-muted-foreground">{sessionInfo.mouseMovements || 0}</div>
                  </div>
                  <div>
                    <span className="font-medium">Keystrokes:</span>
                    <div className="text-muted-foreground">{sessionInfo.keystrokes || 0}</div>
                  </div>
                  <div>
                    <span className="font-medium">Scroll Depth:</span>
                    <div className="text-muted-foreground">{sessionInfo.scrollDepth || 0}%</div>
                  </div>
                  <div>
                    <span className="font-medium">Time on Page:</span>
                    <div className="text-muted-foreground">
                      {sessionInfo.timeOnPage ? `${Math.round(sessionInfo.timeOnPage / 1000)}s` : '0s'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Session Timing</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Session ID:</span>
                    <div className="text-muted-foreground font-mono text-xs">
                      {sessionInfo.sessionId || 'No session ID'}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Page Load Time:</span>
                    <div className="text-muted-foreground">
                      {sessionInfo.pageLoadTime ? `${sessionInfo.pageLoadTime}ms` : 'Unknown'}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Referrer:</span>
                    <div className="text-muted-foreground text-xs">
                      {sessionInfo.referrer || 'Direct access'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span>Security Analysis</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center">
                  <div className="text-3xl font-bold">{attempt.risk_score}</div>
                  <div className="text-sm text-muted-foreground">Risk Score</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{attempt.success ? '✓' : '✗'}</div>
                  <div className="text-sm text-muted-foreground">Success Status</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{deviceInfo.doNotTrack ? '⚠' : '✓'}</div>
                  <div className="text-sm text-muted-foreground">Tracking Policy</div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium">Risk Factors:</h4>
                <div className="space-y-2">
                  {deviceInfo.doNotTrack && (
                    <div className="flex items-center space-x-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm">Do Not Track enabled (+10 risk)</span>
                    </div>
                  )}
                  {!deviceInfo.cookieEnabled && (
                    <div className="flex items-center space-x-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm">Cookies disabled (+15 risk)</span>
                    </div>
                  )}
                  {!attempt.success && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">Failed login attempt (+25 risk)</span>
                    </div>
                  )}
                  {attempt.risk_score < 25 && (
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">Low risk profile</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="raw" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5" />
                <span>Raw Data</span>
              </CardTitle>
              <CardDescription>
                Complete technical details of the login attempt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-96">
                {JSON.stringify({
                  id: attempt.id,
                  user_id: attempt.user_id,
                  phone_number: attempt.phone_number,
                  email: attempt.email,
                  attempt_type: attempt.attempt_type,
                  success: attempt.success,
                  ip_address: attempt.ip_address,
                  user_agent: attempt.user_agent,
                  location_data: attempt.location_data,
                  device_fingerprint: attempt.device_fingerprint,
                  session_data: attempt.session_data,
                  risk_score: attempt.risk_score,
                  created_at: attempt.created_at,
                  updated_at: attempt.updated_at
                }, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};